import { useState, useMemo, Fragment } from 'react';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import LoadingButton from '@mui/lab/LoadingButton';
import EditIcon from '@mui/icons-material/Edit';
import style from '../../common-style/common-style.module.css';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import Box  from '@material-ui/core/Box';
import BmpBsci from '../BSCI/bsci';
import ScoreInsights from '../score-insight/scores-insights';
import useHttp from '../../hooks/useHttp';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import { APPROVED, REJECTED, MESSAGES } from '../../utils/status';
import BmpModal from '../Modal/Bmp-Modal';


const toolTipText = {
    score: "Evaluates the alignment of this output with review data, tone, and brand safety, based on relevance and sentiment scores. Higher scores indicate better alignment with customer feedback and brand guidelines. \n Sentiment Score: A Number showing positive, neutral or negative user sentiment in user feedback overall,. Higher is more positive sentiment . and lower more negative. \n Relevance score: How well this content matches what customers ask and what reviews mention. Higher is better - more on-topic and useful content.",
    bsci: "Determines overall safety & quality rating for this content, measures relevance, tone, usefulness and risk. A higher score indicates lower risk and more alignment with brand standards and safer to publish."
};

const BMPReviewThemeTable = ({ themesData, docId, isRejected, statusColor, mainTableData, locale, setRefreshData, pendingThemeIds, setPendingThemeIds, setSnackbarState }) => {
    const [selectedTheme, setSelectedTheme] = useState([]);
    const [editText, setEditText] = useState({});
    const themes = useMemo(() => themesData || [], [themesData]);
    const { sendRequest, isLoading } = useHttp();
    const [modalInfo, setModalInfo] = useState({ state: '', active: false });
    const numSelected = selectedTheme.length;
    const rowCount = themes.length;
    const isThemeSelected = (id) => selectedTheme.indexOf(id) !== -1;
    const selectedThemeData = themes.filter(item => selectedTheme.includes(item.theme_id))
    const pendingThemeId = pendingThemeIds.includes(docId);
    const { subCollection_uid } = mainTableData.skuSummary;
    const plural = selectedThemeData.length > 1;
    
    const handleEditThemeText = (id, text) => {
        setEditText((prev) => ({
            ...prev,
            [`id_${docId}_${id}`]: true,
            [`load_${docId}_${id}`]: false,
            [`text_${docId}_${id}`]: text,
            [`error_${docId}_${id}`]: null
        }));
    };
    
    const handleSaveTheme = async(id, prevText) => {
        const text = editText[`text_${docId}_${id}`] || '';
        const charLimit = 50;

        if (text === '') {
            const ErrorMessage = 'Theme cannot be empty';
            setEditText((prev) => ({
                ...prev,
                [`error_${docId}_${id}`]: ErrorMessage
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: ErrorMessage,
                severity: 'error'
            }));
            return;
        }

        if (text.length > charLimit) {
            const ErrorMessage = `Theme name cannot be more than ${charLimit} charactors.`;
            setEditText((prev) => ({
                ...prev,
                [`error_${docId}_${id}`]: ErrorMessage
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: ErrorMessage,
                severity: 'error'
            }));
            return;
        }

        if (text === prevText) {
            setEditText((prev) => ({
                ...prev,
                [`id_${docId}_${id}`]: false
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: MESSAGES.noChange,
                severity: 'info'
            }));
            return;
        }

        setEditText((prev) => ({
            ...prev,
            [`load_${docId}_${id}`]: true,
            [`error_${docId}_${id}`]: null
        }));

        try {
            const data = [{
                "subCollection_uid": subCollection_uid,
                "doc_id": docId,
                "action": "save",
                "locale": locale,
                "themes": [
                    {
                        "theme": text, 
                        "theme_id": id
                    }
                ]
            }];
            const response = await sendRequest('summary/update', 'POST', data);
            if (response && response[0].status === 500) {
                throw new Error(response[0].message);
            }
            setRefreshData(true);
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: MESSAGES.saved,
                severity: 'success'
            }));
            setEditText((prev) => ({
                ...prev,
                [`text_${docId}_${id}`]: text,
            }));
        } catch (err) {
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: err.message || MESSAGES.dataFailed,
                severity: 'error'
            }));
        }
        setEditText((prev) => ({
            ...prev,
            [`id_${docId}_${id}`]: false,
            [`load_${docId}_${id}`]: false
        }));
    };

    const handleCancelThemeText = (id) => {
        setEditText((prev) => ({
            ...prev,
            [`text_${docId}_${id}`]: null,
            [`id_${docId}_${id}`]: false,
            [`error_${docId}_${id}`]: null
        }));
    };

    const handleThemeEditChange = (id, newValue) => {
        setEditText((prev) => ({
            ...prev,
            [`text_${docId}_${id}`]: newValue,
            [`error_${docId}_${id}`]: null
        }));
    };

    const handleSelectAllThemeClick = (event) => {
        event.stopPropagation();
        if (event.target.checked) {
            const newSelected = themes.map((n) => n.theme_id);
            setSelectedTheme([...newSelected]);
        } else {
            setSelectedTheme([]);
        }
        const fileredPendingThemeIds = pendingThemeIds.filter(item => item !== docId);
        setPendingThemeIds(fileredPendingThemeIds);
    };
    
    const handleThemeChange = (event, id) => {
        event.stopPropagation();
        let prevSelected = selectedTheme;
        const selectedIndex = prevSelected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(prevSelected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(prevSelected.slice(1));
        } else if (selectedIndex === prevSelected.length - 1) {
            newSelected = newSelected.concat(prevSelected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                prevSelected.slice(0, selectedIndex),
                prevSelected.slice(selectedIndex + 1),
            );
        }
        setSelectedTheme(newSelected);
        const fileredPendingThemeIds = pendingThemeIds.filter(item => item !== docId);
        // console.log(docId, pendingThemeIds, filered);
        setPendingThemeIds(fileredPendingThemeIds);
    };

    const handleOpenModal = (status) => {
        setModalInfo({
            state: status,
            active: true
        });
    }


    const confirmAction = async() => {
        const themeIds = selectedThemeData.map(item => ({ theme_id: item.theme_id }));
        // console.log(selectedThemeData, themeIds);

        const data = [{
            "subCollection_uid": subCollection_uid,
            "doc_id": docId,
            "action": modalInfo.state,
            "locale": locale,
            "themes": themeIds
        }];
        try {
            const response = await sendRequest('summary/update', 'POST', data);
            if (response && response[0].status === 500) {
                throw new Error(response[0].message);
            }
            setRefreshData(true);
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: `Item ${modalInfo.state === 'approve' ? 'Approved' : 'Rejected'} successfully!`,
                severity: 'success'
            }));
        } catch(err) {
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: err.message || MESSAGES.dataFailed,
                severity: 'error'
            }));
            console.error(err);
        }
        
        setModalInfo(prev => ({
            ...prev,
            active:false
        }));
        setSelectedTheme([]);
    }
    
    return (
        <Fragment>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: '5px', mb: 1}}>
                <Button 
                    variant="contained" 
                    color="success" 
                    size='small' 
                    disabled={!(selectedTheme.length > 0)}
                    onClick={() => handleOpenModal('approve')}
                >Approve</Button>
                <Button 
                    variant="contained" 
                    color="error" 
                    size='small' 
                    disabled={!(selectedTheme.length > 0)}
                    onClick={() => handleOpenModal('reject')}
                >Reject</Button>
            </Box>
            
            <Table
                sx={{ 
                    maxWidth:'100%', 
                    minWidth:'100%', 
                    width:'100%', 
                    backgroundColor: 'rgba(241, 241, 241, .5)',
                    boxShadow: '0 0 4px 0 rgba(0,0,0,.2)'
                }}
                size='small'
                className={pendingThemeId ? `${style['pending_theme_selection']}` : ''}
            >
                <TableHead sx={{
                        backgroundColor: 'rgba(241, 241, 241, .7)',
                        ".MuiTableCell-root": {
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color:'#1f36c8',
                            "& :hover": {
                                color:'#1f36c8'
                            },
                            "& :focus": {
                                color:'#1f36c8'
                            },
                        }
                    }}>
                    <TableRow>
                        <TableCell sx={{ padding: 0}}>
                            <Checkbox
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={handleSelectAllThemeClick}
                                inputProps={{
                                    'aria-label': 'Select all reviews themes',
                                }}
                            />
                        </TableCell>
                        <TableCell sx={{ minWidth: '40%', width: '100%'}}>Review Themes</TableCell>
                        <TableCell>
                            <Tooltip 
                                title={toolTipText.score.split('\n').map((line, lineIndex) => <span className={style.tooltipBreak} key={lineIndex}>{line}</span>)}
                            >
                                <span>
                                    Score 
                                    <HelpOutlineIcon 
                                        sx={{cursor: 'pointer', fontSize: '12px', position: 'relative', top: '2px', marginLeft: '1px'}} 
                                    />
                                </span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                            <Tooltip 
                                title={toolTipText.bsci.split('\n').map((line, lineIndex) => <span className={style.tooltipBreak} key={lineIndex}>{line}</span>)}
                            >
                                <span>
                                    BSCI <HelpOutlineIcon sx={{cursor: 'pointer', fontSize: '12px', position: 'relative', top: '2px', marginLeft: '-2px'}} />
                                </span>
                            </Tooltip>
                        </TableCell>
                        {/* <TableCell>Status</TableCell> */}
                    </TableRow>
                </TableHead>

                <TableBody>
                {themes.map((theme, i) => {
                    const themeId = theme.theme_id;
                    const isItemSelected = isThemeSelected(themeId);
                    const themeStatus = isRejected ? 'rejected' : theme.content_status;
                    const labelId = `theme-table-checkbox-${i}`;
                    const bsciScore = theme?.BSCI;

                    return (
                        <TableRow 
                            key={`theme-random-id-${i}`}
                            sx={{
                                backgroundColor: statusColor(themeStatus),
                                "&:hover": {
                                    backgroundColor: statusColor(themeStatus, true)
                                },
                            }}
                        >
                            <TableCell sx={{padding: 0}}>
                                <Checkbox
                                    color="primary"
                                    checked={isItemSelected}
                                    onChange={(event) => handleThemeChange(event, themeId)}
                                    inputProps={{
                                        'aria-labelledby': labelId,
                                    }}
                                />
                            </TableCell>
                            <TableCell className={style['table-left-align']} id={labelId}>
                                {editText[`id_${docId}_${themeId}`] ? (
                                    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '10px'}}>
                                        {/* <TextField 
                                            id={`theme-input-${themeId}`} 
                                            label="" 
                                            variant='filled'
                                            disabled={editText[`load_${docId}_${themeId}`] || false}
                                            value={editText[`text_${docId}_${themeId}`]}
                                            onChange={(e) => handleThemeEditChange(themeId, e.target.value)}
                                            sx={{
                                                width:'100%',
                                            }}
                                        /> */}
                                        <div className={`${editText[`error_${docId}_${themeId}`] ? style.textareaError : ''}`}>
                                            <OutlinedInput
                                                id={`theme-input-${themeId}`} 
                                                label="" 
                                                variant='filled'
                                                size='small'
                                                disabled={editText[`load_${docId}_${themeId}`] || false}
                                                value={editText[`text_${docId}_${themeId}`]}
                                                onChange={(e) => handleThemeEditChange(themeId, e.target.value)}
                                                aria-describedby={labelId}
                                                autoFocus={true}
                                                inputProps={{
                                                    'aria-label': 'Theme'
                                                }}
                                                sx={{
                                                    width:'100%',
                                                    "input": {
                                                        paddingLeft:'5px',
                                                        paddingRight:'5px',
                                                        backgroundColor:'#fff',
                                                        borderRadius: '4px'
                                                    },
                                                    "fieldset": {
                                                        borderColor: 'rgba(0, 0, 0, 0.9)'
                                                    }
                                                }}
                                            />
                                        </div>
                                        {/* <textarea 
                                            className={`${style.themename} ${editText[`error_${docId}_${themeId}`] ? style.textareaError : ''}`}
                                            value={editText[`text_${docId}_${themeId}`]}
                                            onChange={(e) => handleThemeEditChange(themeId, e.target.value)}
                                            disabled={editText[`load_${docId}_${themeId}`] || false}
                                        ></textarea> */}
                                        <Box sx={{flex:1}}>
                                            <LoadingButton
                                                onClick={() => handleSaveTheme(themeId, theme.theme)}
                                                size="small"
                                                // endIcon={<SaveIcon />}
                                                loading={editText[`load_${docId}_${themeId}`] || false}
                                                // loadingPosition="end"
                                                variant="outlined"
                                                aria-label={`Save Theme - ${theme.theme}`}
                                                sx={{
                                                    minWidth: 'auto',
                                                    padding:0,
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '3px'
                                                }}
                                            >
                                                <SaveIcon aria-hidden="true" sx={{width: '1rem', height: '1rem'}} />
                                            </LoadingButton>
                                            <Button 
                                                onClick={() => handleCancelThemeText(themeId)}
                                                size="small"
                                                color="error"
                                                variant="outlined" 
                                                aria-label={`Cancel - ${theme.theme}`}
                                                sx={{
                                                    minWidth: 'auto',
                                                    padding: 0,
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '3px',
                                                    marginTop:'4px'
                                                }}
                                            >
                                                <ClearIcon aria-hidden="true" sx={{width: '1rem', height: '1rem'}} />
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '5px'}}> 
                                        <div>{editText[`text_${docId}_${themeId}`] || theme.theme}</div>
                                        <Button 
                                            onClick={() => handleEditThemeText(themeId, theme.theme)}
                                            size="small"
                                            color="primary" 
                                            variant="outlined" 
                                            aria-label={`Edit Theme - ${theme.theme}`}
                                            sx={{borderRadius: '3px', width: 24, height: 24, minWidth:'auto',
                                                ".MuiSvgIcon-fontSizeMedium": {
                                                    width:'.75em',
                                                    height:'.75em'
                                                }
                                            }}
                                        >
                                            <EditIcon sx={{fontSize:'20px'}} aria-hidden="true" />
                                        </Button>
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell>
                                <ScoreInsights sentiments={theme.sentiment_score} />
                            </TableCell>
                            <TableCell className={style.bsciTd}>
                                <BmpBsci bsci={bsciScore} />
                            </TableCell>
                        </TableRow>
                    )
                })}
                </TableBody>
            </Table>
             
            {modalInfo.active && (
                <BmpModal modalInfo={modalInfo} setModalInfo={setModalInfo} confirmAction={confirmAction} loading={isLoading} plural={plural}>
                    <Typography>
                        <strong>{mainTableData.sku_id}</strong> - <span className={style['popup-title']}>{mainTableData.title}</span>
                    </Typography>
                    {selectedThemeData.map(item => {
                        return (
                            <Fragment key={item.theme_id} >
                                <Typography sx={{ ml: 1.5, mt: 1 }}>
                                    <span 
                                        style={{
                                            paddingLeft: '3px', 
                                            paddingRight: '3px', 
                                            backgroundColor: modalInfo.state === 'approve' && item.content_status === APPROVED ? '#d9efe0' : modalInfo.state === 'reject' && item.content_status === REJECTED ? '#fab090' : ''
                                        }}
                                    >Theme - <strong>{item.theme}</strong></span> 
                                </Typography> 
                            </Fragment>
                        )
                    })}
                </BmpModal>
            )}    

        </Fragment>
        
    )
};

export default BMPReviewThemeTable;